import { useState, useEffect, useMemo, useCallback } from 'react'

interface VirtualListOptions {
  itemHeight: number
  containerHeight: number
  overscan?: number // Number of items to render outside visible area
}

interface VirtualListResult<T> {
  virtualItems: Array<{
    index: number
    item: T
    offsetY: number
  }>
  totalHeight: number
  scrollOffset: number
  setScrollOffset: (offset: number) => void
}

/**
 * Custom hook for virtual scrolling to efficiently render large lists
 *
 * @param items - Array of items to virtualize
 * @param options - Configuration options for virtual scrolling
 * @returns Virtual list utilities and visible items
 */
export const useVirtualList = <T>(
  items: T[],
  options: VirtualListOptions
): VirtualListResult<T> => {
  const { itemHeight, containerHeight, overscan = 3 } = options
  const [scrollOffset, setScrollOffsetState] = useState(0)
  const setScrollOffset = useCallback((offset: number) => {
    setScrollOffsetState(offset)
  }, [])

  const totalHeight = useMemo(() => {
    return items.length * itemHeight
  }, [items.length, itemHeight])

  const visibleRange = useMemo(() => {
    const startIndex = Math.floor(scrollOffset / itemHeight)
    const endIndex = Math.min(
      startIndex + Math.ceil(containerHeight / itemHeight),
      items.length - 1
    )

    return {
      start: Math.max(0, startIndex - overscan),
      end: Math.min(items.length - 1, endIndex + overscan),
    }
  }, [scrollOffset, itemHeight, containerHeight, overscan, items.length])

  const virtualItems = useMemo(() => {
    const result: Array<{
      index: number
      item: T
      offsetY: number
    }> = []

    for (let i = visibleRange.start; i <= visibleRange.end; i++) {
      if (items[i]) {
        result.push({
          index: i,
          item: items[i],
          offsetY: i * itemHeight,
        })
      }
    }

    return result
  }, [visibleRange, items, itemHeight])

  useEffect(() => {
    setScrollOffsetState(0)
  }, [items])

  return {
    virtualItems,
    totalHeight,
    scrollOffset,
    setScrollOffset,
  }
}

/**
 * Hook for determining if virtual scrolling should be used based on dataset size
 *
 * @param itemCount - Number of items in the list
 * @param threshold - Threshold above which to use virtual scrolling (default: 100)
 * @returns Whether virtual scrolling should be enabled
 */
export const useShouldVirtualize = (
  itemCount: number,
  threshold: number = 100
): boolean => {
  return useMemo(() => itemCount > threshold, [itemCount, threshold])
}
